import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Search } from "lucide-react";

import Ring from "../ui/charts/Ring";
import { Empty, Kpi, Legend, Section } from "../ui/primitives";
import { STATE_COLORS, WELLNESS_STATES, fmtNumber, fmtPercent, fmtPeriod } from "../ui/reportFormat";
import { ReportHeader } from "../ui/reportSections";

const RESTRICTED = "restricted";

const initials = (name) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

/** Folds accents so "matias" finds "Matías". */
const fold = (text) => text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const alertChips = (a) => {
  const chips = [];
  if (a.score !== null && a.score <= 40) chips.push("Alto riesgo");
  else if (a.score !== null && a.score <= 60) chips.push("Precaución");
  if (a.acwr !== null && a.acwr > 1.5) chips.push("Carga alta");
  if (a.wellness?.sleep_hours != null && a.wellness.sleep_hours < 6.5) chips.push("Sueño bajo");
  return chips;
};

const AthleteCard = ({ athlete: a, to }) => {
  const speed = a.metrics.find((m) => m.unit.toLowerCase() === "km/h");
  const chips = alertChips(a);
  return (
    <Link
      to={to}
      className="flex flex-col gap-3 rounded-xl border border-neutral-200 bg-white p-4 transition-shadow hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-200"
    >
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-navy-900 text-sm font-semibold text-white">
          {initials(a.full_name)}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-navy-900">{a.full_name}</p>
          <p className="truncate text-[12.5px] text-ink">{[a.category, a.position].filter(Boolean).join(" · ")}</p>
        </div>
        {a.data_sharing ? (
          <span className="rounded-full bg-[#0f8a6a1a] px-2 py-0.5 text-[11px] font-medium text-[#0f8a6a]">Comparte datos</span>
        ) : (
          <span className="rounded-full bg-paper px-2 py-0.5 text-[11px] font-medium text-ink">Sin autorización</span>
        )}
      </div>

      <div className="flex items-center gap-3">
        {a.data_sharing && a.score !== null ? (
          <>
            <div className="w-14 shrink-0">
              <Ring value={a.score} color={a.state?.color} />
            </div>
            <p className="font-display text-[20px] font-semibold" style={{ color: a.state?.color }}>
              {a.state?.name}
            </p>
          </>
        ) : (
          <p className="text-[13px] text-ink">
            {a.data_sharing ? "Sin check-ins en el periodo" : a.availability ? `Disponibilidad: ${a.availability.label}` : "Solo rendimiento"}
          </p>
        )}
      </div>

      <dl className="grid grid-cols-3 gap-2 text-center">
        {[
          ["Índice dep.", a.performance_index === null ? "—" : `${a.performance_index}`],
          [speed ? speed.title : "Días con sesión", speed ? `${fmtNumber(speed.latest, 1)} ${speed.unit}` : `${a.sessions_recorded}`],
          ["Carga 7d", a.load_7d === null ? "—" : `${fmtNumber(a.load_7d)} UA`],
        ].map(([label, value]) => (
          <div key={label} className="rounded-md bg-paper px-1.5 py-2">
            <dt className="truncate text-[11px] text-peri-300">{label}</dt>
            <dd className="m-0 text-[13.5px] font-semibold text-navy-900">{value}</dd>
          </div>
        ))}
      </dl>

      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {chips.map((chip) => (
            <span key={chip} className="rounded-full bg-red-50 px-2 py-0.5 text-[11.5px] font-medium text-alert-300">
              {chip}
            </span>
          ))}
        </div>
      )}
    </Link>
  );
};

/** "Mis deportistas" — the coach's view of their academy's confirmed roster. */
const AcademyRosterReport = ({ data, detailPath }) => {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const t = data.totals;

  const filtered = useMemo(() => {
    const needle = fold(search.trim());
    return data.athletes
      .filter((a) => !needle || fold(a.full_name).includes(needle))
      .filter((a) => !category || a.category === category)
      .filter((a) => {
        if (!status) return true;
        if (status === RESTRICTED) return !a.data_sharing;
        return a.state?.name === status;
      })
      .sort((x, y) => (x.score ?? 101) - (y.score ?? 101) || x.full_name.localeCompare(y.full_name, "es"));
  }, [data.athletes, search, category, status]);

  const statusFilters = [
    { value: "", label: "Todos" },
    ...WELLNESS_STATES.map((s) => ({ value: s.name, label: s.name, color: s.color })),
    { value: RESTRICTED, label: "Sin autorización" },
  ];
  const distributionTotal = data.state_distribution.reduce((sum, s) => sum + s.count, 0);

  return (
    <>
      <ReportHeader
        eyebrow="Reporte de grupo · Vista de entrenador"
        title="Mis deportistas"
        subtitle={`${data.coach_name ?? "Entrenador"} · ${data.academy.name}`}
        chips={[{ label: `Últimos ${data.period.days} días` }, { label: `${t.athletes} deportistas` }]}
        meta={[
          ["Academia", data.academy.name],
          ["Periodo", fmtPeriod(data.period)],
        ]}
        notice="Los deportistas que autorizaron compartir su check-in muestran estado, bienestar y carga. Los demás muestran solo métricas de rendimiento."
      />

      <Section title="Resumen">
        <div className="grid grid-cols-2 gap-3.5 lg:grid-cols-4">
          <Kpi label="Deportistas a cargo" value={t.athletes} note="Confirmados en la academia" />
          <Kpi
            label="En precaución o riesgo"
            value={t.at_risk}
            note="Índice de estado de 60% o menos"
            color={t.at_risk > 0 ? STATE_COLORS.danger : STATE_COLORS.ok}
          />
          <Kpi label="Sin autorización" value={t.without_consent} note="No comparten su check-in" />
          <Kpi label="Adherencia promedio" value={fmtPercent(t.avg_adherence)} note="Días con check-in" />
        </div>

        <div className="mt-5">
          <p className="mb-1.5 text-[12px] text-peri-300">Distribución de estados · solo quienes comparten su check-in</p>
          {distributionTotal === 0 ? (
            <Empty>Sin check-ins en el periodo.</Empty>
          ) : (
            <>
              <div className="flex h-3.5 overflow-hidden rounded-md" role="img" aria-label="Distribución de estados del grupo">
                {data.state_distribution.map((s) => (
                  <span key={s.name} style={{ flexGrow: s.count, background: s.color }} />
                ))}
              </div>
              <Legend items={data.state_distribution.map((s) => ({ label: s.name, color: s.color, count: s.count }))} />
            </>
          )}
        </div>
      </Section>

      {data.priority.length > 0 && (
        <Section title="Atención prioritaria" meta="Estado crítico o salto de carga">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {data.priority.map((p) => (
              <Link
                key={p.athlete_id}
                to={detailPath(p.athlete_id)}
                className="flex items-center gap-3 rounded-lg bg-red-50 px-3 py-2.5 hover:bg-red-100"
              >
                <AlertTriangle size={18} className="shrink-0 text-alert-300" aria-hidden="true" />
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-navy-900">
                    {p.full_name}
                    {p.state && p.score !== null && (
                      <span className="font-normal text-ink">
                        {" "}
                        · {p.state.name} {p.score}%
                      </span>
                    )}
                  </p>
                  <p className="text-[12.5px] text-ink">{p.reason}</p>
                </div>
              </Link>
            ))}
          </div>
        </Section>
      )}

      <Section title="Deportistas" meta={`${filtered.length} de ${data.athletes.length}`}>
        <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center">
          <label className="relative block lg:w-64">
            <span className="sr-only">Buscar deportista</span>
            <Search size={16} className="absolute top-1/2 left-3 -translate-y-1/2 text-peri-300" aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar deportista"
              className="w-full rounded-lg border border-neutral-300 py-2 pr-3 pl-9 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-200"
            />
          </label>
          {data.categories.length > 1 && (
            <label className="flex items-center gap-2 text-sm text-ink">
              Categoría
              <select
                value={category}
                onChange={(event) => setCategory(event.target.value)}
                className="rounded-lg border border-neutral-300 px-3 py-2 text-sm text-navy-900"
              >
                <option value="">Todas</option>
                {data.categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
          )}
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filtrar por estado">
            {statusFilters.map((f) => (
              <button
                key={f.value || "all"}
                type="button"
                aria-pressed={status === f.value}
                onClick={() => setStatus(f.value)}
                className={`rounded-full border px-3 py-1 text-[12.5px] ${
                  status === f.value ? "border-navy-900 bg-navy-900 text-white" : "border-neutral-300 text-ink hover:bg-paper"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <Empty>
            {data.athletes.length === 0
              ? "Aún no hay deportistas confirmados en tu academia."
              : "Ningún deportista coincide con los filtros."}
          </Empty>
        ) : (
          <div className="grid gap-3.5 sm:grid-cols-2 xl:grid-cols-3">
            {filtered.map((athlete) => (
              <AthleteCard key={athlete.athlete_id} athlete={athlete} to={detailPath(athlete.athlete_id)} />
            ))}
          </div>
        )}
      </Section>
    </>
  );
};

export default AcademyRosterReport;
